/**
 * Intake Templates Domain Seed Data
 *
 * Contains seed data for Loan Intake Wizard Templates:
 * - System templates (Bridge, Construction, Rental/DSCR, Commercial, Partner Invitation)
 * - Template configurations with step definitions
 * - Compliance questions per template
 * - Business-level investment type configurations
 *
 * @see Issue #432 - Templated Kiavi-Style Loan Intake Wizard
 * @see Issue #526 - Allow business admins to configure investment types
 */

import { seedId } from '../utils';
import { BUSINESS_IDS, REALM_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// INTAKE TEMPLATE IDs
// =============================================================================

export const INTAKE_TEMPLATE_IDS = {
  /** Bridge/Fix & Flip loan intake template */
  BRIDGE: seedId('intake-template-bridge', 1),
  /** Ground-up construction loan intake template */
  CONSTRUCTION: seedId('intake-template-construction', 2),
  /** Rental/DSCR loan intake template */
  RENTAL_DSCR: seedId('intake-template-rental', 3),
  /** Commercial loan intake template */
  COMMERCIAL: seedId('intake-template-commercial', 4),
  /** General consultation (short form) template */
  GENERAL: seedId('intake-template-general', 5),
  /** Partner invitation/onboarding template */
  PARTNER_INVITATION: seedId('intake-template-partner', 6),
} as const;

export type IntakeTemplateId = (typeof INTAKE_TEMPLATE_IDS)[keyof typeof INTAKE_TEMPLATE_IDS];

// =============================================================================
// COMPLIANCE QUESTION IDs
// =============================================================================

export const COMPLIANCE_QUESTION_IDS = {
  // Bridge/Fix-Flip questions
  BRIDGE_PROPERTY_OWNED: seedId('compliance-bridge-owned', 1),
  BRIDGE_CLEAR_TITLE: seedId('compliance-bridge-title', 2),
  BRIDGE_INSURANCE: seedId('compliance-bridge-insurance', 3),
  BRIDGE_EXIT_STRATEGY: seedId('compliance-bridge-exit', 4),

  // Construction questions
  CONSTRUCTION_PERMITS: seedId('compliance-const-permits', 5),
  CONSTRUCTION_CONTRACTOR: seedId('compliance-const-contractor', 6),
  CONSTRUCTION_PLANS: seedId('compliance-const-plans', 7),
  CONSTRUCTION_BUDGET: seedId('compliance-const-budget', 8),

  // Rental/DSCR questions
  RENTAL_PROPERTY_TYPE: seedId('compliance-rental-type', 9),
  RENTAL_CURRENT_LEASE: seedId('compliance-rental-lease', 10),
  RENTAL_MARKET_RENTS: seedId('compliance-rental-market', 11),
  RENTAL_PROPERTY_MGMT: seedId('compliance-rental-mgmt', 12),

  // Commercial questions
  COMMERCIAL_ZONING: seedId('compliance-comm-zoning', 13),
  COMMERCIAL_ENVIRONMENTAL: seedId('compliance-comm-env', 14),
  COMMERCIAL_TENANTS: seedId('compliance-comm-tenants', 15),
  COMMERCIAL_FINANCIALS: seedId('compliance-comm-financials', 16),

  // Universal compliance questions
  UNIVERSAL_CREDIT_AUTH: seedId('compliance-credit-auth', 17),
  UNIVERSAL_BACKGROUND: seedId('compliance-background', 18),
  UNIVERSAL_BANKRUPTCY: seedId('compliance-bankruptcy', 19),
  UNIVERSAL_FORECLOSURE: seedId('compliance-foreclosure', 20),
} as const;

export type ComplianceQuestionId = (typeof COMPLIANCE_QUESTION_IDS)[keyof typeof COMPLIANCE_QUESTION_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export type IntakeStepType =
  | 'investment_type'
  | 'entity_experience'
  | 'property_address'
  | 'property_details'
  | 'deal_financials'
  | 'borrower_financials'
  | 'permit_status'
  | 'builder_details'
  | 'certificate_of_occupancy'
  | 'rate_calculator'
  | 'compliance'
  | 'signing_date'
  | 'guarantor_details'
  | 'ssn_verification'
  | 'contact_info'
  | 'project_description'
  | 'construction_details'
  | 'rental_details'
  | 'commercial_details';

export interface IntakeStepDefinition {
  order: number;
  type: IntakeStepType;
  title: string;
  description?: string;
  isRequired: boolean;
  isConditional?: boolean;
  conditionField?: string;
  conditionValue?: string | boolean;
  validationSchema?: string; // Reference to Zod schema
}

export interface ComplianceQuestionDefinition {
  id: string;
  questionText: string;
  helpText?: string;
  isRequired: boolean;
  disqualifyingAnswer?: boolean; // If answer matches this, borrower is disqualified
  category: 'property' | 'borrower' | 'legal' | 'financial';
}

export interface IntakeTemplateSeedData {
  id: string;
  businessId: string;
  realmId: string;
  code: string;
  name: string;
  description: string;
  investmentType: 'bridge' | 'construction' | 'rental' | 'commercial' | 'general' | 'partner_invitation';
  version: string;
  isSystemTemplate: boolean;
  isActive: boolean;
  isDefault: boolean;
  steps: IntakeStepDefinition[];
  complianceQuestions: ComplianceQuestionDefinition[];
  defaultRateConfig: {
    baseRate: number;
    maxLTV: number;
    maxLTC: number;
    minLoanAmount: number;
    maxLoanAmount: number;
    termOptions: number[];
  };
  brandingConfig?: {
    primaryColor?: string;
    logoUrl?: string;
    headerText?: string;
    footerText?: string;
  };
  createdBy: string;
}

export interface InvestmentTypeConfigSeedData {
  id: string;
  businessId: string;
  realmId: string;
  supportedInvestmentTypes: ('bridge' | 'construction' | 'rental' | 'commercial')[];
  alwaysShowGeneral: boolean;
  defaultTemplateId?: string;
  customRedirectUrl?: string;
  enableAdvisorRouting: boolean;
  advisorEmail?: string;
  createdBy: string;
}

// =============================================================================
// COMPLIANCE QUESTIONS DATA
// =============================================================================

const BRIDGE_COMPLIANCE_QUESTIONS: ComplianceQuestionDefinition[] = [
  {
    id: COMPLIANCE_QUESTION_IDS.BRIDGE_PROPERTY_OWNED,
    questionText: 'Is this property currently owned by you or under contract?',
    helpText: 'We need to verify ownership or contractual rights before proceeding.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.BRIDGE_CLEAR_TITLE,
    questionText: 'To the best of your knowledge, does the property have a clear title?',
    helpText: 'Properties with liens, judgments, or title issues may require additional review.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.BRIDGE_INSURANCE,
    questionText: 'Are you able to obtain property insurance for this property?',
    helpText: 'Insurance is required at closing. Properties in high-risk areas may need specialized coverage.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.BRIDGE_EXIT_STRATEGY,
    questionText: 'Do you have a defined exit strategy (sale or refinance)?',
    helpText: 'Bridge loans are short-term and require a clear plan for repayment.',
    isRequired: true,
    category: 'financial',
  },
];

const CONSTRUCTION_COMPLIANCE_QUESTIONS: ComplianceQuestionDefinition[] = [
  {
    id: COMPLIANCE_QUESTION_IDS.CONSTRUCTION_PERMITS,
    questionText: 'Have you obtained or applied for all necessary building permits?',
    helpText: 'Construction loans require proper permitting. We can assist with permit review.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.CONSTRUCTION_CONTRACTOR,
    questionText: 'Do you have a licensed general contractor engaged for this project?',
    helpText: 'Ground-up construction typically requires a licensed GC. Owner-builder may be considered.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.CONSTRUCTION_PLANS,
    questionText: 'Do you have approved architectural plans and specifications?',
    helpText: 'Detailed plans are required for construction draw schedules and inspections.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.CONSTRUCTION_BUDGET,
    questionText: 'Have you prepared a detailed construction budget with contractor bids?',
    helpText: 'A line-item budget helps determine loan amounts and draw schedules.',
    isRequired: true,
    category: 'financial',
  },
];

const RENTAL_COMPLIANCE_QUESTIONS: ComplianceQuestionDefinition[] = [
  {
    id: COMPLIANCE_QUESTION_IDS.RENTAL_PROPERTY_TYPE,
    questionText: 'Is this property a 1-4 unit residential or 5+ unit multifamily?',
    helpText: 'Different DSCR requirements apply based on property type.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.RENTAL_CURRENT_LEASE,
    questionText: 'If currently rented, are there existing leases in place?',
    helpText: 'Existing leases help verify income for DSCR calculations.',
    isRequired: false,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.RENTAL_MARKET_RENTS,
    questionText: 'Have you obtained a rent survey or market rent analysis?',
    helpText: 'Market rents are used to verify DSCR on vacant properties.',
    isRequired: false,
    category: 'financial',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.RENTAL_PROPERTY_MGMT,
    questionText: 'Do you plan to self-manage or use professional property management?',
    helpText: 'Property management plans help assess ongoing operations.',
    isRequired: false,
    category: 'property',
  },
];

const COMMERCIAL_COMPLIANCE_QUESTIONS: ComplianceQuestionDefinition[] = [
  {
    id: COMPLIANCE_QUESTION_IDS.COMMERCIAL_ZONING,
    questionText: 'Is the property properly zoned for its current/intended use?',
    helpText: 'Commercial properties must have appropriate zoning for the loan type.',
    isRequired: true,
    category: 'property',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.COMMERCIAL_ENVIRONMENTAL,
    questionText: 'Has a Phase I environmental assessment been completed?',
    helpText: 'Most commercial loans require Phase I ESA. We can coordinate if needed.',
    isRequired: false,
    category: 'legal',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.COMMERCIAL_TENANTS,
    questionText: 'If income-producing, do you have current rent rolls and tenant information?',
    helpText: 'Tenant quality and lease terms affect commercial property valuations.',
    isRequired: false,
    category: 'financial',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.COMMERCIAL_FINANCIALS,
    questionText: 'Can you provide 2+ years of operating statements (if existing property)?',
    helpText: 'Historical financials help verify income and expense projections.',
    isRequired: false,
    category: 'financial',
  },
];

const UNIVERSAL_COMPLIANCE_QUESTIONS: ComplianceQuestionDefinition[] = [
  {
    id: COMPLIANCE_QUESTION_IDS.UNIVERSAL_CREDIT_AUTH,
    questionText: 'Do you authorize us to pull your credit report for loan qualification?',
    helpText: 'A credit check is required for all borrowers and guarantors.',
    isRequired: true,
    category: 'borrower',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.UNIVERSAL_BACKGROUND,
    questionText: 'Have you ever been convicted of a felony?',
    helpText: 'Certain convictions may affect loan eligibility.',
    isRequired: true,
    disqualifyingAnswer: true,
    category: 'borrower',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.UNIVERSAL_BANKRUPTCY,
    questionText: 'Have you filed for bankruptcy in the last 4 years?',
    helpText: 'Recent bankruptcy may affect eligibility or require additional documentation.',
    isRequired: true,
    category: 'borrower',
  },
  {
    id: COMPLIANCE_QUESTION_IDS.UNIVERSAL_FORECLOSURE,
    questionText: 'Have you had a foreclosure, short sale, or deed-in-lieu in the last 4 years?',
    helpText: 'Recent adverse credit events may affect eligibility or pricing.',
    isRequired: true,
    category: 'borrower',
  },
];

// =============================================================================
// STEP DEFINITIONS BY TEMPLATE TYPE
// =============================================================================

const BRIDGE_STEPS: IntakeStepDefinition[] = [
  {
    order: 1,
    type: 'investment_type',
    title: 'Investment Type',
    description: 'Select your investment strategy',
    isRequired: true,
    validationSchema: 'InvestmentTypeStepSchema',
  },
  {
    order: 2,
    type: 'contact_info',
    title: 'Contact Information',
    description: 'Tell us how to reach you',
    isRequired: true,
    validationSchema: 'ContactInfoStepSchema',
  },
  {
    order: 3,
    type: 'property_details',
    title: 'Property Details',
    description: 'Describe the subject property (address, sqft, bed/bath)',
    isRequired: true,
    validationSchema: 'PropertyDetailsStepSchema',
  },
  {
    order: 4,
    type: 'deal_financials',
    title: 'Deal Financials',
    description: 'Purchase price, rehab costs, ARV, existing liens, exit strategy',
    isRequired: true,
    validationSchema: 'DealFinancialsStepSchema',
  },
  {
    order: 5,
    type: 'permit_status',
    title: 'Permit Status',
    description: 'Permitting status for the project',
    isRequired: false,
  },
  {
    order: 6,
    type: 'certificate_of_occupancy',
    title: 'Certificate of Occupancy',
    description: 'CO status (primarily for bridge loans)',
    isRequired: false,
  },
  {
    order: 7,
    type: 'borrower_financials',
    title: 'Borrower Information',
    description: 'Entity/principal name, credit, liquidity, net worth, track record',
    isRequired: true,
    validationSchema: 'BorrowerFinancialsStepSchema',
  },
  {
    order: 8,
    type: 'entity_experience',
    title: 'Entity & Experience',
    description: 'Tell us about your borrowing entity and investment experience',
    isRequired: true,
    validationSchema: 'EntityExperienceStepSchema',
  },
  {
    order: 9,
    type: 'compliance',
    title: 'Qualification Questions',
    description: 'Answer a few questions to verify eligibility',
    isRequired: true,
    validationSchema: 'ComplianceStepSchema',
  },
  {
    order: 10,
    type: 'signing_date',
    title: 'Closing Timeline',
    description: 'When do you need to close?',
    isRequired: true,
    validationSchema: 'SigningDateStepSchema',
  },
  {
    order: 11,
    type: 'guarantor_details',
    title: 'Guarantor Information',
    description: 'Provide guarantor details',
    isRequired: true,
    isConditional: true,
    conditionField: 'hasGuarantor',
    conditionValue: 'yes',
    validationSchema: 'GuarantorDetailsStepSchema',
  },
  {
    order: 12,
    type: 'ssn_verification',
    title: 'Identity Verification',
    description: 'Verify your identity to proceed',
    isRequired: true,
    validationSchema: 'SsnVerificationStepSchema',
  },
];

const CONSTRUCTION_STEPS: IntakeStepDefinition[] = [
  {
    order: 1,
    type: 'investment_type',
    title: 'Investment Type',
    description: 'Select your construction project type',
    isRequired: true,
    validationSchema: 'InvestmentTypeStepSchema',
  },
  {
    order: 2,
    type: 'contact_info',
    title: 'Contact Information',
    description: 'Tell us how to reach you',
    isRequired: true,
    validationSchema: 'ContactInfoStepSchema',
  },
  {
    order: 3,
    type: 'property_details',
    title: 'Property Details',
    description: 'Describe the subject property (address, sqft, bed/bath)',
    isRequired: true,
    validationSchema: 'PropertyDetailsStepSchema',
  },
  {
    order: 4,
    type: 'deal_financials',
    title: 'Deal Financials',
    description: 'Purchase price, construction costs, ARV, existing liens',
    isRequired: true,
    validationSchema: 'DealFinancialsStepSchema',
  },
  {
    order: 5,
    type: 'permit_status',
    title: 'Permit Status',
    description: 'Permitting status for the project',
    isRequired: true,
  },
  {
    order: 6,
    type: 'builder_details',
    title: 'Builder & GC Details',
    description: 'Construction experience and general contractor information',
    isRequired: true,
  },
  {
    order: 7,
    type: 'borrower_financials',
    title: 'Borrower Information',
    description: 'Entity/principal name, credit, liquidity, net worth, track record',
    isRequired: true,
    validationSchema: 'BorrowerFinancialsStepSchema',
  },
  {
    order: 8,
    type: 'entity_experience',
    title: 'Entity & Experience',
    description: 'Tell us about your entity and construction experience',
    isRequired: true,
    validationSchema: 'EntityExperienceStepSchema',
  },
  {
    order: 9,
    type: 'compliance',
    title: 'Project Readiness',
    description: 'Verify your project is ready to proceed',
    isRequired: true,
    validationSchema: 'ComplianceStepSchema',
  },
  {
    order: 10,
    type: 'signing_date',
    title: 'Closing Timeline',
    description: 'When do you need to start construction?',
    isRequired: true,
    validationSchema: 'SigningDateStepSchema',
  },
  {
    order: 11,
    type: 'guarantor_details',
    title: 'Guarantor Information',
    description: 'Provide guarantor details',
    isRequired: true,
    isConditional: true,
    conditionField: 'hasGuarantor',
    conditionValue: 'yes',
    validationSchema: 'GuarantorDetailsStepSchema',
  },
  {
    order: 12,
    type: 'ssn_verification',
    title: 'Identity Verification',
    description: 'Verify your identity to proceed',
    isRequired: true,
    validationSchema: 'SsnVerificationStepSchema',
  },
];

const RENTAL_DSCR_STEPS: IntakeStepDefinition[] = [
  {
    order: 1,
    type: 'investment_type',
    title: 'Investment Type',
    description: 'Confirm rental/DSCR loan selection',
    isRequired: true,
    validationSchema: 'InvestmentTypeStepSchema',
  },
  {
    order: 2,
    type: 'entity_experience',
    title: 'Entity & Portfolio',
    description: 'Tell us about your entity and rental portfolio',
    isRequired: true,
    validationSchema: 'EntityExperienceStepSchema',
  },
  {
    order: 3,
    type: 'property_address',
    title: 'Property Details',
    description: 'Where is the rental property?',
    isRequired: true,
    validationSchema: 'PropertyAddressStepSchema',
  },
  {
    order: 4,
    type: 'rental_details',
    title: 'Rental Information',
    description: 'Tell us about the rental income',
    isRequired: true,
  },
  {
    order: 5,
    type: 'rate_calculator',
    title: 'DSCR Calculator',
    description: 'Calculate your DSCR and loan terms',
    isRequired: true,
    validationSchema: 'RateCalculatorStepSchema',
  },
  {
    order: 6,
    type: 'compliance',
    title: 'Property Verification',
    description: 'Answer questions about the rental property',
    isRequired: true,
    validationSchema: 'ComplianceStepSchema',
  },
  {
    order: 7,
    type: 'signing_date',
    title: 'Closing Timeline',
    description: 'When do you need to close?',
    isRequired: true,
    validationSchema: 'SigningDateStepSchema',
  },
  {
    order: 8,
    type: 'ssn_verification',
    title: 'Identity Verification',
    description: 'Verify your identity to proceed',
    isRequired: true,
    validationSchema: 'SsnVerificationStepSchema',
  },
];

const COMMERCIAL_STEPS: IntakeStepDefinition[] = [
  {
    order: 1,
    type: 'investment_type',
    title: 'Commercial Loan Type',
    description: 'What type of commercial property?',
    isRequired: true,
    validationSchema: 'InvestmentTypeStepSchema',
  },
  {
    order: 2,
    type: 'entity_experience',
    title: 'Entity & Experience',
    description: 'Tell us about your commercial real estate experience',
    isRequired: true,
    validationSchema: 'EntityExperienceStepSchema',
  },
  {
    order: 3,
    type: 'property_address',
    title: 'Property Location',
    description: 'Where is the commercial property?',
    isRequired: true,
    validationSchema: 'PropertyAddressStepSchema',
  },
  {
    order: 4,
    type: 'commercial_details',
    title: 'Commercial Details',
    description: 'Tell us about the commercial property',
    isRequired: true,
  },
  {
    order: 5,
    type: 'rate_calculator',
    title: 'Loan Structure',
    description: 'Calculate your commercial loan terms',
    isRequired: true,
    validationSchema: 'RateCalculatorStepSchema',
  },
  {
    order: 6,
    type: 'compliance',
    title: 'Due Diligence',
    description: 'Commercial property verification questions',
    isRequired: true,
    validationSchema: 'ComplianceStepSchema',
  },
  {
    order: 7,
    type: 'signing_date',
    title: 'Closing Timeline',
    description: 'When do you need to close?',
    isRequired: true,
    validationSchema: 'SigningDateStepSchema',
  },
  {
    order: 8,
    type: 'guarantor_details',
    title: 'Guarantor Information',
    description: 'Commercial loans typically require a personal guarantee',
    isRequired: true,
    validationSchema: 'GuarantorDetailsStepSchema',
  },
  {
    order: 9,
    type: 'ssn_verification',
    title: 'Identity Verification',
    description: 'Verify your identity to proceed',
    isRequired: true,
    validationSchema: 'SsnVerificationStepSchema',
  },
];

const GENERAL_CONSULTATION_STEPS: IntakeStepDefinition[] = [
  {
    order: 1,
    type: 'investment_type',
    title: 'How Can We Help?',
    description: 'Tell us about your project',
    isRequired: true,
    validationSchema: 'InvestmentTypeStepSchema',
  },
  {
    order: 2,
    type: 'contact_info',
    title: 'Contact Information',
    description: 'How can we reach you?',
    isRequired: true,
  },
  {
    order: 3,
    type: 'project_description',
    title: 'Project Details',
    description: 'Describe your project or question',
    isRequired: true,
  },
];

// =============================================================================
// INTAKE TEMPLATE DATA
// =============================================================================

export const INTAKE_TEMPLATES: Record<keyof typeof INTAKE_TEMPLATE_IDS, IntakeTemplateSeedData> = {
  BRIDGE: {
    id: INTAKE_TEMPLATE_IDS.BRIDGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'bridge-v2',
    name: 'Bridge/Fix & Flip',
    description:
      'Short-term financing for property acquisition, renovation, and resale. Ideal for fix-and-flip investors and bridge financing needs.',
    investmentType: 'bridge',
    version: '2.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: true,
    steps: BRIDGE_STEPS,
    complianceQuestions: [...BRIDGE_COMPLIANCE_QUESTIONS, ...UNIVERSAL_COMPLIANCE_QUESTIONS],
    defaultRateConfig: {
      baseRate: 11.0,
      maxLTV: 75,
      maxLTC: 90,
      minLoanAmount: 75000,
      maxLoanAmount: 5000000,
      termOptions: [6, 9, 12, 18, 24],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  CONSTRUCTION: {
    id: INTAKE_TEMPLATE_IDS.CONSTRUCTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'construction-v2',
    name: 'Ground-Up Construction',
    description:
      'Financing for new construction projects from land acquisition through vertical build. Includes draw management and inspection services.',
    investmentType: 'construction',
    version: '2.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: false,
    steps: CONSTRUCTION_STEPS,
    complianceQuestions: [...CONSTRUCTION_COMPLIANCE_QUESTIONS, ...UNIVERSAL_COMPLIANCE_QUESTIONS],
    defaultRateConfig: {
      baseRate: 12.5,
      maxLTV: 70,
      maxLTC: 85,
      minLoanAmount: 150000,
      maxLoanAmount: 10000000,
      termOptions: [12, 18, 24, 36],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  RENTAL_DSCR: {
    id: INTAKE_TEMPLATE_IDS.RENTAL_DSCR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'rental-dscr-v1',
    name: 'Rental/DSCR',
    description:
      'Long-term financing for rental properties based on property cash flow (DSCR). No personal income verification required.',
    investmentType: 'rental',
    version: '1.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: false,
    steps: RENTAL_DSCR_STEPS,
    complianceQuestions: [...RENTAL_COMPLIANCE_QUESTIONS, ...UNIVERSAL_COMPLIANCE_QUESTIONS],
    defaultRateConfig: {
      baseRate: 8.5,
      maxLTV: 80,
      maxLTC: 100,
      minLoanAmount: 100000,
      maxLoanAmount: 3000000,
      termOptions: [60, 120, 180, 240, 300, 360],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  COMMERCIAL: {
    id: INTAKE_TEMPLATE_IDS.COMMERCIAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'commercial-v1',
    name: 'Commercial',
    description: 'Financing for commercial real estate including office, retail, industrial, and mixed-use properties.',
    investmentType: 'commercial',
    version: '1.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: false,
    steps: COMMERCIAL_STEPS,
    complianceQuestions: [...COMMERCIAL_COMPLIANCE_QUESTIONS, ...UNIVERSAL_COMPLIANCE_QUESTIONS],
    defaultRateConfig: {
      baseRate: 9.0,
      maxLTV: 70,
      maxLTC: 85,
      minLoanAmount: 250000,
      maxLoanAmount: 25000000,
      termOptions: [12, 24, 36, 60, 120],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  GENERAL: {
    id: INTAKE_TEMPLATE_IDS.GENERAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'general-v1',
    name: 'General Consultation',
    description:
      'Not sure which loan product fits? Submit a quick consultation request and our team will reach out to discuss your options.',
    investmentType: 'general',
    version: '1.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: false,
    steps: GENERAL_CONSULTATION_STEPS,
    complianceQuestions: [],
    defaultRateConfig: {
      baseRate: 0,
      maxLTV: 0,
      maxLTC: 0,
      minLoanAmount: 0,
      maxLoanAmount: 0,
      termOptions: [],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },

  PARTNER_INVITATION: {
    id: INTAKE_TEMPLATE_IDS.PARTNER_INVITATION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    code: 'partner-v1',
    name: 'Partner Invitation',
    description: 'Onboarding form for new referral partners, brokers, and wholesale partners.',
    investmentType: 'partner_invitation',
    version: '1.0.0',
    isSystemTemplate: true,
    isActive: true,
    isDefault: false,
    steps: [
      {
        order: 1,
        type: 'contact_info',
        title: 'Partner Information',
        description: 'Tell us about yourself and your company',
        isRequired: true,
      },
      {
        order: 2,
        type: 'entity_experience',
        title: 'Business Details',
        description: 'Company information and licensing',
        isRequired: true,
      },
    ],
    complianceQuestions: [],
    defaultRateConfig: {
      baseRate: 0,
      maxLTV: 0,
      maxLTC: 0,
      minLoanAmount: 0,
      maxLoanAmount: 0,
      termOptions: [],
    },
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// INVESTMENT TYPE CONFIG IDs
// =============================================================================

export const INVESTMENT_TYPE_CONFIG_IDS = {
  OPSMATRIX_CAPITAL: seedId('investment-config-opsmatrix', 1),
  ONE_SHARPE_CAPITAL: seedId('investment-config-1sharpe', 2),
  PACIFIC_BRIDGE_FUND: seedId('investment-config-pacific', 3),
  GULF_COAST_FO: seedId('investment-config-gulfcoast', 4),
} as const;

export type InvestmentTypeConfigId = (typeof INVESTMENT_TYPE_CONFIG_IDS)[keyof typeof INVESTMENT_TYPE_CONFIG_IDS];

// =============================================================================
// INVESTMENT TYPE CONFIGURATION DATA
// =============================================================================

export const INVESTMENT_TYPE_CONFIGS: Record<keyof typeof INVESTMENT_TYPE_CONFIG_IDS, InvestmentTypeConfigSeedData> = {
  OPSMATRIX_CAPITAL: {
    id: INVESTMENT_TYPE_CONFIG_IDS.OPSMATRIX_CAPITAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    supportedInvestmentTypes: ['bridge', 'construction', 'rental', 'commercial'],
    alwaysShowGeneral: true,
    defaultTemplateId: INTAKE_TEMPLATE_IDS.BRIDGE,
    enableAdvisorRouting: true,
    advisorEmail: 'loans@opsmatrixcapital.com',
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  ONE_SHARPE_CAPITAL: {
    id: INVESTMENT_TYPE_CONFIG_IDS.ONE_SHARPE_CAPITAL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    realmId: REALM_IDS.LOAN_SERVICING,
    supportedInvestmentTypes: ['bridge', 'construction'],
    alwaysShowGeneral: true,
    defaultTemplateId: INTAKE_TEMPLATE_IDS.BRIDGE,
    enableAdvisorRouting: true,
    advisorEmail: 'lending@1sharpe.com',
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  PACIFIC_BRIDGE_FUND: {
    id: INVESTMENT_TYPE_CONFIG_IDS.PACIFIC_BRIDGE_FUND,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    realmId: REALM_IDS.LOAN_SERVICING,
    supportedInvestmentTypes: ['bridge', 'rental'],
    alwaysShowGeneral: true,
    defaultTemplateId: INTAKE_TEMPLATE_IDS.BRIDGE,
    enableAdvisorRouting: false,
    createdBy: USER_IDS.SUPER_ADMIN,
  },
  GULF_COAST_FO: {
    id: INVESTMENT_TYPE_CONFIG_IDS.GULF_COAST_FO,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    realmId: REALM_IDS.LOAN_SERVICING,
    supportedInvestmentTypes: ['bridge', 'commercial'],
    alwaysShowGeneral: true,
    defaultTemplateId: INTAKE_TEMPLATE_IDS.COMMERCIAL,
    enableAdvisorRouting: true,
    advisorEmail: 'investments@gcfo.com',
    createdBy: USER_IDS.SUPER_ADMIN,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all intake templates as an array for seeding
 */
export function getAllIntakeTemplates(): IntakeTemplateSeedData[] {
  return Object.values(INTAKE_TEMPLATES);
}

/**
 * Get all investment type configurations as an array for seeding
 */
export function getAllInvestmentTypeConfigs(): InvestmentTypeConfigSeedData[] {
  return Object.values(INVESTMENT_TYPE_CONFIGS);
}

/**
 * Get templates by investment type
 */
export function getTemplatesByInvestmentType(
  investmentType: 'bridge' | 'construction' | 'rental' | 'commercial' | 'general',
): IntakeTemplateSeedData[] {
  return Object.values(INTAKE_TEMPLATES).filter((t) => t.investmentType === investmentType);
}

/**
 * Get active templates for a business
 */
export function getActiveTemplatesForBusiness(businessId: string): IntakeTemplateSeedData[] {
  return Object.values(INTAKE_TEMPLATES).filter((t) => t.businessId === businessId && t.isActive);
}

/**
 * Get investment type config for a business
 */
export function getInvestmentTypeConfigForBusiness(businessId: string): InvestmentTypeConfigSeedData | undefined {
  return Object.values(INVESTMENT_TYPE_CONFIGS).find((c) => c.businessId === businessId);
}

/**
 * Get default template for a business
 */
export function getDefaultTemplateForBusiness(businessId: string): IntakeTemplateSeedData | undefined {
  return Object.values(INTAKE_TEMPLATES).find((t) => t.businessId === businessId && t.isDefault);
}

/**
 * Get all compliance questions by category
 */
export function getComplianceQuestionsByCategory(
  category: 'property' | 'borrower' | 'legal' | 'financial',
): ComplianceQuestionDefinition[] {
  const allQuestions = [
    ...BRIDGE_COMPLIANCE_QUESTIONS,
    ...CONSTRUCTION_COMPLIANCE_QUESTIONS,
    ...RENTAL_COMPLIANCE_QUESTIONS,
    ...COMMERCIAL_COMPLIANCE_QUESTIONS,
    ...UNIVERSAL_COMPLIANCE_QUESTIONS,
  ];
  return allQuestions.filter((q) => q.category === category);
}
